import { Component, OnDestroy, ViewChild } from '@angular/core';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@capacitor/status-bar';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { IonicModule, IonRouterOutlet, NavController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Settings } from 'luxon';
import { register } from 'swiper/element/bundle';
import { MemoryUsageDivComponent } from '../core/components/memory-usage-div/memory-usage-div.component';
import { HttpErrorHandler } from '../core/http/httpErrorHandler';
import { DaoProvider } from '../core/persistence/dao.provider';
import { ServersConnectionsProvider } from '../core/providers/serversConnections.provider';
import { AppStateService } from '../core/services/appState.service';
import { DefaultDataDownloaderService } from '../core/services/defaultDataDownloader.service';
import { EventsService } from '../core/services/events.service';
import { OfflineModeService } from '../core/services/offlineMode.service';
import { UserService } from '../core/services/user.service';
import { KeyValueDbDao } from '../gyzmo-commons/dao/db/keyValue.db.dao';
import { MOCK_VERSION_KEY } from '../gyzmo-commons/dtos/mockVersion.dto';
import { DeviceHelper, PlatformName } from '../gyzmo-commons/helpers/device.helper';
import { LoadingHelper } from '../gyzmo-commons/helpers/loading.helper';
import { NetworkHelper } from '../gyzmo-commons/helpers/network.helper';
import { isNullOrEmpty } from '../gyzmo-commons/helpers/null.helper';
import { ToastHelper } from '../gyzmo-commons/helpers/toast.helper';
import { DatabaseLocation } from '../gyzmo-commons/interfaces/databaseLocation';
import { IAppConfigurationProvider } from '../gyzmo-commons/interfaces/IAppConfigurationProvider';
import { KeyValue } from '../gyzmo-commons/models/keyValue.model';
import { Version } from '../gyzmo-commons/models/version.model';
import { AppSqlProvider } from '../gyzmo-commons/persistence/app.sql.provider';
import { Database } from '../gyzmo-commons/persistence/database';
import { AppStateManagementService } from '../gyzmo-commons/services/appStateManagement.service';
import { AppUpdateService } from '../gyzmo-commons/services/appUpdate.service';
import { AppVersionService } from '../gyzmo-commons/services/appVersion.service';
import { BackButtonManagementService } from '../gyzmo-commons/services/backButtonManagement.service';
import { CacheService } from '../gyzmo-commons/services/cache.service';
import { DatabaseVersionService } from '../gyzmo-commons/services/databaseVersion.service';
import { GeolocationService } from '../gyzmo-commons/services/geolocation.service';
import { KeyboardServiceAndroid } from '../gyzmo-commons/services/keyboard.service.android';
import { KeyboardServiceIos } from '../gyzmo-commons/services/keyboard.service.ios';
import { LanguageService } from '../gyzmo-commons/services/language.service';
import { LoggerService } from '../gyzmo-commons/services/logs/logger.service';
import { PingService } from '../gyzmo-commons/services/ping.service';
import { ThemeService } from '../gyzmo-commons/services/theme.service';
import { MODULE } from './app.constants';

register();

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    imports: [IonicModule, MemoryUsageDivComponent],
})
export class AppComponent implements OnDestroy {
    @ViewChild(IonRouterOutlet, { static: true }) routerOutlet: IonRouterOutlet;
    isInitialized = false;
    logout = false;
    login: { user: string; password: string } = null;
    mockConfig = '';
    showDebugInfo = false;
    private resumeListener: PluginListenerHandle;

    constructor(platform: Platform,
                eventsService: EventsService,
                private logger: LoggerService,
                private appSqlProvider: AppSqlProvider,
                private database: Database,
                private navController: NavController,
                private deviceHelper: DeviceHelper,
                private loadingHelper: LoadingHelper,
                private networkHelper: NetworkHelper,
                private appVersionService: AppVersionService,
                private databaseVersionService: DatabaseVersionService,
                private userService: UserService,
                private translateService: TranslateService,
                private toastHelper: ToastHelper,
                private httpErrorHandler: HttpErrorHandler,
                private serversConnectionsProvider: ServersConnectionsProvider,
                private languageService: LanguageService,
                private offlineModeService: OfflineModeService,
                private daoProvider: DaoProvider,
                private cacheService: CacheService,
                private keyValueDbDao: KeyValueDbDao,
                private geolocationService: GeolocationService,
                private appUpdateService: AppUpdateService,
                private appStateService: AppStateService,
                private pingService: PingService,
                private themeService: ThemeService,
                private appConfigurationProvider: IAppConfigurationProvider,
                private backButtonManagementService: BackButtonManagementService,
                //private mocksHelper: MocksHelper,
                private defaultDataDownloaderService: DefaultDataDownloaderService,
                private appStateManagementService: AppStateManagementService) {
        this.logout = !isNullOrEmpty(this.getParameterByName('logout'));
        if (!isNullOrEmpty(this.getParameterByName('login'))) {
            let param = this.getParameterByName('login').split(/[ +]/);
            this.login = { user: param[0], password: param[1] };
        }
        if (!isNullOrEmpty(this.getParameterByName('mockConfig'))) {
            this.mockConfig = this.getParameterByName('mockConfig');
        }

        eventsService.getActivateDebugModeSubjectObservable()
            .subscribe(() => {
                this.showDebugInfo = !this.showDebugInfo;
            });

        void platform.ready().then(async () => {
            await this.initialize();
            await this.start();
            await this.afterStart();
        });
    }

    ngOnDestroy(): void {
        if (this.resumeListener) {
            void this.resumeListener.remove();
            this.resumeListener = null;
        }
    }

    async initialize() {
        await this.deviceHelper.initialize();

        if (this.deviceHelper.isRunningOnDevice()) {
            await this.themeService.initialize();
        }

        await this.navController.navigateRoot('/splashscreen');

        if (this.deviceHelper.isRunningOnDevice()) {
            await SplashScreen.hide();
        }
        this.logger.initialize();
        this.networkHelper.initialize();

        this.logger.info(this.constructor.name, 'App is running on ', this.deviceHelper.getPlatform());

        this.logger.info(this.constructor.name, 'User languages : ' + this.languageService.getUserLanguages());

        // this language will be used as a fallback when a translation isn't found in the current language
        this.translateService.setDefaultLang('en');

        // the lang to use, if the lang isn't available, it will use the current loader to get them
        this.changeLanguage(this.languageService.getUserLanguage());

        // Mettre après l'initialisation du language pour que le message de maj bdd soit traduis
        await this.initializeSql();

        this.backButtonManagementService.initialize();

        this.navController.setTopOutlet(this.routerOutlet);

        await this.serversConnectionsProvider.initialize();

        this.httpErrorHandler.getUserDisconnectedObservable()
            .subscribe(async () => {
                await this.userService.disconnect(false)
                    .then(async userWasConnected => {
                        if (userWasConnected) {
                            await this.toastHelper.show(this.translateService.instant('IONIC_You_ve_been_disconnected_please_reconnect.'));
                        }
                        await this.appConfigurationProvider.navigateToConnection();
                    });
            });

        // Cache un éventuel loading affiché par la mise à jour de la bdd
        await this.loadingHelper.hideAll();
        this.isInitialized = true;
        this.logger.info(this.constructor.name, '✅ **************************** App is initialized ****************************');
    }

    /**
     * Re-asserts the edge-to-edge state of the status bar (WebView overlay).
     *
     * On some devices (old Huawei EMUI with a modern Chromium WebView), the
     * "recent apps" transition or the return to the foreground breaks the overlay
     * state: the layout is shifted by the status bar height and the WebView
     * collapses to ~96px on the first keyboard focus, which the user perceives as
     * a blank screen. Calling overlaysWebView(true) again restores the correct state.
     *
     * Idempotent (no-op when the state is already correct) and Android only, so it
     * has no effect on healthy devices nor on iOS.
     */
    public async reassertStatusBarOverlay() {
        if (this.deviceHelper.getPlatform() != PlatformName.ANDROID) {
            return;
        }

        await StatusBar.setOverlaysWebView({ overlay: true });
    }

    private async afterStart() {
        await this.geolocationService.initialize();

        this.pingService.startLoop(this.serversConnectionsProvider.getServerConnection());

        this.offlineModeService.startSynchronisationTask();

        await this.appStateManagementService.initialize();

        if (this.deviceHelper.isRunningOnDevice()) {
            await CapacitorUpdater.notifyAppReady();

            if (this.deviceHelper.getPlatform() == PlatformName.ANDROID) {
                await KeyboardServiceAndroid.start();
            } else {
                await KeyboardServiceIos.start();
            }

            setTimeout(async () => {
                await this.appUpdateService.askForUpdateIfNeeded();
            }, 3000);
        }

        this.resumeListener = await App.addListener('resume', () => {
            // Restores the edge-to-edge state broken by the "recent apps" transition
            // or by the return to the foreground on some devices (old Huawei EMUI with
            // a modern WebView): without it the layout is shifted and the WebView
            // collapses when the keyboard opens (perceived as a blank screen).
            // Idempotent, Android only.
            void this.reassertStatusBarOverlay();
        });
    }

    private changeLanguage(language: string) {
        this.logger.info(this.constructor.name, 'Set language to ' + language);
        this.translateService.use(language);
        Settings.defaultLocale = language;
    }

    private getParameterByName(name: any) {
        let url = window.location.href;
        name = name.replace(/[[]]/g, '$&');
        let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/[+]/g, ' '));
    }

    private async initializeSql() {
        // Okay, so the platform is ready and our plugins are available.
        // Here you can do any higher level native things you might need.
        await this.appSqlProvider.initialize(MODULE, DatabaseLocation.default, this.deviceHelper.getDeviceSerial());

        this.logger.info(this.constructor.name, '✅ **************************** SQL is initialized ****************************');

        let appVersionCode = await this.appVersionService.getAppVersionCode();
        let dbVersion: number = 0;
        if ((await this.appSqlProvider.getExistingTables()).indexOf(Version.TABLENAME) >= 0) {
            dbVersion = Number((await this.databaseVersionService.getDatabaseVersion()).version);
        }
        this.logger.info(this.constructor.name, 'Database Version=' + dbVersion);
        this.logger.info(this.constructor.name, 'App Version=' + appVersionCode);

        await this.database.initialize(
            this.daoProvider.getAllDaoList(),
            this.daoProvider.getOnDisconnectDaoList(),
            appVersionCode,
            dbVersion);

        if (dbVersion != 0 && appVersionCode > dbVersion) {
            if (await this.userService.isConnected()) {
                await this.userService.disconnect(false);
            }
        }

        await this.cacheService.cleanCache();
        await this.appStateService.clean();
    }

    private async start() {
        if (!isNullOrEmpty(this.mockConfig)) {
            await this.keyValueDbDao.save(new KeyValue(MOCK_VERSION_KEY, this.mockConfig));
        }

        if (this.logout) {
            await this.userService.disconnect(true);
            await this.appConfigurationProvider.navigateToConnection();
        } else if (this.login) {
            await this.userService.disconnect(false);

            let connectionResult = await this.userService.connect(this.login.user, this.login.password);

            this.appSqlProvider.enableBulkWriting();
            await this.defaultDataDownloaderService.onConnectionDefaultData(connectionResult.user);
            await this.appSqlProvider.commitBulk();

            await this.keyValueDbDao.save(new KeyValue('LAST_LOGIN', connectionResult.user.username));
            await this.keyValueDbDao.save(new KeyValue('CGU_' + connectionResult.user.username, '1'));
            await this.keyValueDbDao.save(new KeyValue('PRIVACY_POLICY_' + connectionResult.user.username, '1'));

            await this.navController.navigateRoot('/about');
        } else {
            let user = await this.userService.getCurrentUserWithThirdPartyOnly();
            if (user) {
                let cguAccepted = await this.keyValueDbDao.get('CGU_' + user.username);
                let privacyPolicyAccepted = await this.keyValueDbDao.get('PRIVACY_POLICY_' + user.username);

                if (cguAccepted == null || cguAccepted.value != '1') {
                    await this.navController.navigateRoot('/cgu');
                } else if (privacyPolicyAccepted == null || privacyPolicyAccepted.value != '1') {
                    await this.navController.navigateRoot('/privacy-policy');
                } else {
                    //await this.mocksHelper.navigateToMock();
                    await this.appConfigurationProvider.navigateAfterConnection();
                }
            } else {
                await this.appConfigurationProvider.navigateToConnection();
            }

            if (this.deviceHelper.isRunningOnDevice()) {
                await CapacitorUpdater.notifyAppReady();
            }
        }
    }
}
